/* eslint-disable no-nested-ternary */
/* eslint-disable no-continue */
// @ts-nocheck
/**
 * ESM twin of `lib/miniatureUtils.js` (CJS) for the Vite admin-front / browser bundles.
 * Keep the two in sync — same pure geometry, no shared module (the server uses the CJS one via
 * require/app.locals; Vite dev serves native ESM and can't default-import a CommonJS file).
 * See lib/miniatureUtils.js for the full rationale on the mm→px scale.
 */

export const FALLBACK_PX = 220;

/**
 * The real-world horizontal extent (mm) the iso bounding box's screen width represents — the
 * footprint diagonal √(lengthMm² + widthMm²). The physical anchor for all miniature scaling. In an
 * iso ¾ view the box width tracks the vehicle footprint (length-dominated), NOT widthMm.
 * @param {{lengthMm?:number, widthMm?:number}|null|undefined} dimensions
 * @returns {number|null}
 */
export function boxAnchorMm(dimensions) {
  if (!dimensions) return null;
  const L = dimensions.lengthMm;
  const W = dimensions.widthMm;
  if (L > 0 && W > 0) return Math.sqrt(L * L + W * W);
  return L > 0 ? L : (W > 0 ? W : null);
}

/**
 * Display width (px) for a miniature image so its saved bounding box renders at `pxPerMm`.
 * @param {{w:number}|null|undefined} box
 * @param {number|null|undefined} boxWidthMm
 * @param {number} pxPerMm
 * @returns {number}
 */
export function scaledWidthPx(box, boxWidthMm, pxPerMm) {
  if (box && boxWidthMm && pxPerMm > 0 && box.w > 0) {
    return (boxWidthMm * pxPerMm) / box.w;
  }
  return FALLBACK_PX;
}

/**
 * The box's screen footprint at pxPerMm = 1 (see CJS twin for the derivation).
 * @returns {{unitW:number, unitH:number|null}|null}
 */
export function boxFootprintPerMm(box, boxWidthMm, imgW, imgH) {
  if (!box || !boxWidthMm || !(box.w > 0)) return null;
  let unitH = null;
  if (imgW > 0 && imgH > 0 && box.h > 0) {
    unitH = (boxWidthMm * box.h * imgH) / (box.w * imgW);
  }
  return { unitW: boxWidthMm, unitH };
}

/**
 * Shared mm→px scale so the LARGEST bounding box across `miniatures` fits the given bound(s).
 * @param {Array<{overlayBox:object, dimensions:object, imgW:number, imgH:number}>} miniatures
 * @param {{maxWidth?:number, maxHeight?:number}} [bounds]
 * @returns {number}
 */
export function computeSharedScale(miniatures, { maxWidth, maxHeight } = {}) {
  let maxUnitW = 0;
  let maxUnitH = 0;
  for (const m of miniatures || []) {
    if (!m) continue;
    const fp = boxFootprintPerMm(m.overlayBox, boxAnchorMm(m.dimensions), m.imgW, m.imgH);
    if (!fp) continue;
    if (fp.unitW > maxUnitW) maxUnitW = fp.unitW;
    if (fp.unitH != null && fp.unitH > maxUnitH) maxUnitH = fp.unitH;
  }

  const candidates = [];
  if (maxWidth > 0 && maxUnitW > 0) candidates.push(maxWidth / maxUnitW);
  if (maxHeight > 0 && maxUnitH > 0) candidates.push(maxHeight / maxUnitH);
  return candidates.length ? Math.min(...candidates) : 0;
}
